import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { 
  ChevronLeft, Loader2, Search, Calendar, Clock,
  User, Euro, Filter
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Card, CardContent } from '../../components/ui/card';
import { Input } from '../../components/ui/input';
import { Badge } from '../../components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const STATUS_CONFIG = {
  pending: { label: 'En attente', variant: 'secondary' },
  confirmed: { label: 'Confirmé', variant: 'default' },
  paid: { label: 'Payé', variant: 'default' },
  completed: { label: 'Terminé', variant: 'outline' },
  cancelled: { label: 'Annulé', variant: 'destructive' },
};

export default function AdminBookings() {
  const navigate = useNavigate();
  const { token } = useAuth();
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('all');
  const [search, setSearch] = useState('');

  const authHeader = { 'Authorization': `Bearer ${token || localStorage.getItem('token')}` };

  useEffect(() => {
    const fetchBookings = async () => {
      setLoading(true);
      try {
        const params = new URLSearchParams();
        if (statusFilter && statusFilter !== 'all') params.set('status', statusFilter);
        const res = await fetch(`${API_URL}/api/admin/bookings?${params}`, { headers: authHeader });
        if (res.ok) {
          const data = await res.json();
          setBookings(data.bookings || data || []);
        }
      } catch (e) { console.error(e); }
      finally { setLoading(false); }
    };
    fetchBookings();
  }, [statusFilter]);

  const filtered = bookings.filter(b => {
    if (!search) return true;
    const s = search.toLowerCase();
    return b.client_name?.toLowerCase().includes(s) || 
           b.provider_name?.toLowerCase().includes(s) ||
           b.booking_id?.toLowerCase().includes(s);
  });

  if (loading) {
    return (
      <div className="min-h-screen bg-background pt-24 pb-16 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-accent" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background pt-24 pb-16" data-testid="admin-bookings">
      <div className="container-app max-w-5xl">
        <div className="flex items-center gap-3 mb-8">
          <Button variant="ghost" size="sm" onClick={() => navigate('/admin/dashboard')}>
            <ChevronLeft className="w-4 h-4" />
          </Button>
          <div>
            <h1 className="font-heading text-2xl sm:text-3xl font-semibold">Gestion des réservations</h1>
            <p className="text-muted-foreground text-sm">{bookings.length} réservation{bookings.length !== 1 ? 's' : ''} au total</p>
          </div>
        </div>

        {/* Filters */}
        <div className="flex gap-3 mb-6">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <Input 
              placeholder="Rechercher par client, prestataire..." 
              value={search} 
              onChange={e => setSearch(e.target.value)} 
              className="pl-10"
              data-testid="search-bookings"
            />
          </div>
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="w-44" data-testid="booking-status-filter">
              <SelectValue placeholder="Statut" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Tous les statuts</SelectItem>
              <SelectItem value="pending">En attente</SelectItem>
              <SelectItem value="confirmed">Confirmé</SelectItem>
              <SelectItem value="paid">Payé</SelectItem>
              <SelectItem value="completed">Terminé</SelectItem>
              <SelectItem value="cancelled">Annulé</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {/* Bookings List */}
        {filtered.length === 0 ? (
          <div className="text-center py-16">
            <Calendar className="w-12 h-12 text-muted-foreground/20 mx-auto mb-3" />
            <p className="text-muted-foreground">Aucune réservation trouvée</p>
          </div>
        ) : (
          <div className="space-y-3">
            {filtered.map(booking => {
              const statusCfg = STATUS_CONFIG[booking.status] || STATUS_CONFIG.pending;
              return (
                <Card key={booking.booking_id} data-testid={`booking-row-${booking.booking_id}`}>
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-4 flex-1 min-w-0">
                        <div className="w-10 h-10 rounded-xl bg-accent/10 flex items-center justify-center flex-shrink-0">
                          <Calendar className="w-5 h-5 text-accent" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 mb-1">
                            <p className="font-medium truncate">{booking.booking_id?.slice(0, 16)}...</p>
                            <Badge variant={statusCfg.variant}>{statusCfg.label}</Badge>
                          </div>
                          <div className="flex flex-wrap gap-3 text-xs text-muted-foreground">
                            <span className="flex items-center gap-1"><User className="w-3 h-3" />Client: {booking.client_name || 'N/A'}</span>
                            <span className="flex items-center gap-1"><User className="w-3 h-3" />Prestataire: {booking.provider_name || 'N/A'}</span>
                            <span className="flex items-center gap-1"><Calendar className="w-3 h-3" />{booking.date || 'N/A'}</span>
                            <span className="flex items-center gap-1"><Clock className="w-3 h-3" />{booking.start_time || ''} ({booking.duration_hours || 0}h)</span>
                          </div>
                        </div>
                      </div>
                      <div className="text-right flex-shrink-0 ml-4">
                        <p className="font-semibold text-accent">{booking.total_price?.toFixed(0) || 0}€</p>
                        <p className="text-xs text-muted-foreground">{booking.service_category || ''}</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
